import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import {
  RegPlate, StatusText, Icon, Skeleton, EmptyState,
  ListControls, useListPrefs, type SortOption,
} from '../components/ui';
import { api, type Job } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { formatDate } from '../../../shared/schedule';
import './screens.css';

type Filter = 'open' | 'complete' | 'invoiced' | 'all';
type Sort = 'newest' | 'oldest' | 'value' | 'vehicle';

const SORTS: SortOption<Sort>[] = [
  { key: 'newest', label: 'Newest first', hint: 'Most recently opened' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'value', label: 'Highest value' },
  { key: 'vehicle', label: 'Vehicle A-Z' },
];

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'complete', label: 'Complete' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'all', label: 'All' },
];

export default function Jobs() {
  const [prefs, setPrefs, resetPrefs] = useListPrefs('jobs', {
    filter: 'open' as Filter,
    sort: 'newest' as Sort,
  });
  const { filter, sort } = prefs;
  const { data, loading, error, reload } = useLoad<{ jobs: Job[] }>(() => api.get('/jobs'));

  const jobs = useMemo(() => {
    let list = data?.jobs ?? [];
    if (filter === 'open') list = list.filter((j) => j.status === 'open' || j.status === 'in_progress');
    else if (filter !== 'all') list = list.filter((j) => j.status === filter);

    const id = (j: Job) => (j.reg_no ?? j.fleet_no ?? '').toUpperCase();
    const sorted = [...list];
    switch (sort) {
      case 'oldest':
        sorted.sort((a, b) => a.opened_at.localeCompare(b.opened_at));
        break;
      case 'value':
        sorted.sort((a, b) => Number(b.total) - Number(a.total));
        break;
      case 'vehicle':
        sorted.sort((a, b) => id(a).localeCompare(id(b)) || b.opened_at.localeCompare(a.opened_at));
        break;
      default:
        sorted.sort((a, b) => b.opened_at.localeCompare(a.opened_at));
    }
    return sorted;
  }, [data, filter, sort]);

  const openValue = useMemo(
    () =>
      (data?.jobs ?? [])
        .filter((j) => j.status === 'complete')
        .reduce((sum, j) => sum + Number(j.total), 0),
    [data],
  );

  return (
    <Screen
      onRefresh={reload}
      title="Workshop"
      subtitle="Repairs, servicing and job cards"
      action={
        <Link to="/jobs/new" className="btn btn-primary btn-sm">
          <Icon name="plus" size={17} />
          Job
        </Link>
      }
    >
      <div className="stack-4">
        {openValue > 0 ? (
          <div className="notice notice-info">
            <span className="small">
              <strong className="money">£{openValue.toFixed(2)}</strong> of completed work is ready to
              invoice. <Link to="/invoices" style={{ textDecoration: 'underline' }}>Go to invoices</Link>
            </span>
          </div>
        ) : null}

        <ListControls<Filter, Sort>
          chips={FILTERS}
          chip={filter}
          onChip={(key) => setPrefs({ filter: key })}
          sortOptions={SORTS}
          sort={sort}
          onSort={(key) => setPrefs({ sort: key })}
          activeFilterCount={(filter !== 'open' ? 1 : 0) + (sort !== 'newest' ? 1 : 0)}
          onClearFilters={resetPrefs}
        />

        {error ? <div className="notice notice-danger">{error}</div> : null}
        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && jobs.length === 0 ? (
          <EmptyState
            title="No jobs here"
            body="Record repairs and servicing so the maintenance history stays complete."
            action={
              <Link to="/jobs/new" className="btn btn-primary">
                Open a job
              </Link>
            }
          />
        ) : null}

        {jobs.length > 0 && (
          <div className="panel divide">
            {jobs.map((job) => (
              <Link key={job.id} to={`/jobs/${job.id}`} className="listrow">
                <div className="listrow-lead">
                  <RegPlate reg={job.reg_no} fleetNo={job.fleet_no} size="sm" />
                </div>
                <div className="grow">
                  <div className="listrow-title truncate">{job.title}</div>
                  <div className="listrow-sub truncate">
                    <span className="mono tiny">{job.ref}</span>
                    <span className="dot" />
                    {formatDate(job.opened_at)}
                    <span className="dot" />
                    <span className="money">£{Number(job.total).toFixed(2)}</span>
                  </div>
                </div>
                <StatusText
                  state={job.status === 'invoiced' || job.status === 'complete' ? 'ok' : job.status === 'in_progress' ? 'due' : 'unknown'}
                >
                  {job.status === 'in_progress' ? 'In progress' : job.status[0].toUpperCase() + job.status.slice(1)}
                </StatusText>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Screen>
  );
}
